import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AriaOrb } from "../components/AriaOrb";
import { toast } from "sonner";
import { api, saveToken } from "../lib/api";
import { Shield, Sparkles } from "lucide-react";

export default function Login() {
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@demo.com");
  const [password, setPassword] = useState("Judysanjay@100");
  const [busy, setBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/app/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSso = async () => {
    const input = (email && email.includes("@")) ? email
      : prompt("Enter your work email to sign in via SSO:", "any.scientist@demo.com");
    if (!input) return;
    setSsoBusy(true);
    try {
      const { data } = await api.get("/sso/discover", { params: { email: input } });
      if (!data.sso) {
        toast.error(data.error || "No SSO configured for this email domain.");
        setSsoBusy(false); return;
      }
      if (data.mode === "real_okta" && data.authorize_url) {
        // Real Okta OIDC: full-page redirect to the configured IdP /authorize.
        window.location.href = data.authorize_url;
        return;
      }
      // Demo/simulated IdP page
      window.location.href = `${data.idp_url}?org=${encodeURIComponent(data.org_id)}&email=${encodeURIComponent(input)}`;
    } catch (e) {
      toast.error("SSO lookup failed");
      setSsoBusy(false);
    }
  };

  const tryDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: "admin@demo.com", password: "Judysanjay@100" });
      saveToken(data.token);
      await checkAuth();
      toast.success("Demo loaded");
      navigate("/app/home");
    } catch { toast.error("Demo unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[var(--aria-bg)]">
      <div className="hidden lg:flex lg:col-span-2 flex-col justify-between p-12 bg-[#030711] text-white relative overflow-hidden">
        <div className="absolute inset-0 jarvis-grid opacity-40" />
        <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[var(--aria-primary)]/40 blur-3xl" />
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[var(--aria-accent)]/20 blur-3xl" />
        <div className="absolute inset-y-0 left-1/3 right-1/3 spectro-bands opacity-25 pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3">
          <AriaOrb size={40} />
          <span className="font-display text-2xl tracking-tight">ARIA</span>
          <span className="aria-pill ml-1 border-white/20 text-white/70 bg-white/5">Private Workspace</span>
        </div>

        <div className="relative z-10 space-y-7">
          <div className="aria-pill aria-pill-primary border-[var(--aria-primary)]/70 bg-[var(--aria-primary)]/15 text-[var(--aria-accent-glow)]">Dr. Shankar's ARIA</div>
          <h2 className="font-display text-5xl leading-[1.02] tracking-[-0.045em] font-medium">
            Sign in to your<br/>academic<br/>intelligence hub.
          </h2>
          <p className="text-slate-300 max-w-md leading-relaxed">
            Every lecture, every note, every interview — one orb. Pick up exactly where your learning left off.
          </p>
          <div className="flex items-center gap-3 pt-4 border-t border-white/10">
            <div className="flex -space-x-2">
              <div className="w-7 h-7 rounded-full bg-[var(--aria-primary)] border-2 border-[#030711] flex items-center justify-center text-[10px] font-medium">DS</div>
            </div>
            <span className="text-xs text-slate-400 font-mono">Private — built for one student.</span>
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between text-xs text-slate-500 font-mono">
          <div>© ARIA · Private workspace</div>
          <div className="flex items-center gap-3">
            <span>Encrypted</span>·<span>Single-user</span>·<span>v2026.09</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-3 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-5 fade-in" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-2"><AriaOrb size={32} /><span className="font-display text-xl">ARIA</span></div>
          <div>
            <h1 className="font-display text-3xl tracking-tight mb-1">Welcome back</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Sign in to ARIA to continue your research.</p>
          </div>

          <button type="button" onClick={tryDemo} data-testid="demo-login-button" disabled={busy}
            className="btn w-full py-2.5 rounded-md bg-[#18181B] text-white hover:bg-[#27272A] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
            <Sparkles size={14} /> Continue as Dr. Shankar (demo)
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={onGoogle} data-testid="google-login-button"
              className="btn py-2.5 rounded-md border border-[var(--aria-border-strong)] hover:border-[var(--aria-text)] text-sm font-medium flex items-center justify-center gap-2 transition-colors">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="" />
              Google
            </button>
            <button type="button" onClick={onSso} data-testid="sso-login-button" disabled={ssoBusy}
              className="btn py-2.5 rounded-md border border-[var(--aria-border-strong)] hover:border-[var(--aria-primary)] hover:text-[var(--aria-primary)] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              <Shield size={14} /> {ssoBusy ? "Redirecting…" : "SAML / OKTA"}
            </button>
          </div>

          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-faint)] font-mono">
            <div className="h-px flex-1 bg-[var(--aria-border)]" /> or email <div className="h-px flex-1 bg-[var(--aria-border)]" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-medium uppercase tracking-[0.15em] text-[var(--aria-text-muted)] font-mono">Work email</label>
            <input data-testid="login-email" value={email} onChange={e=>setEmail(e.target.value)} type="email" required
              className="w-full px-3 py-2.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] focus:ring-2 focus:ring-[var(--aria-primary)] focus:border-transparent outline-none transition-all" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-medium uppercase tracking-[0.15em] text-[var(--aria-text-muted)] font-mono">Password</label>
            <input data-testid="login-password" value={password} onChange={e=>setPassword(e.target.value)} type="password" required
              className="w-full px-3 py-2.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] focus:ring-2 focus:ring-[var(--aria-primary)] focus:border-transparent outline-none transition-all" />
          </div>
          <button data-testid="login-submit" disabled={busy} type="submit"
            className="btn btn-primary w-full py-2.5 disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-sm text-center text-[var(--aria-text-muted)]">
            No account? <Link to="/signup" className="text-[var(--aria-primary)] font-medium hover:underline">Sign up</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
